import { Badge, Button, Card, cn } from '@design-system';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import PolicyApplicationFormSection from '@client/components/policy-application/PolicyApplicationFormSection';
import { PreviewNavigationProvider } from './preview-navigation-provider';
import {
  useFormDefinition,
  useCurrentSectionIndex,
  useCurrentSubsectionIndex,
  useCanGoNext,
  useCanGoPrevious,
  getFormBuilderActions,
} from '@/features/quote-and-apply-builder';

interface FormPreviewContentProps {
  formData: PolicyFormSubmissionData;
  onFormDataChange: (questionRef: string, value: PolicyFormSubmissionDataValue) => void;
}

export function FormPreviewContent({ formData, onFormDataChange }: FormPreviewContentProps) {
  const formDefinition = useFormDefinition();
  const currentSectionIndex = useCurrentSectionIndex();
  const currentSubsectionIndex = useCurrentSubsectionIndex();
  const canGoNext = useCanGoNext();
  const canGoPrevious = useCanGoPrevious();
  const { navigateNext, navigatePrevious, navigateTo } = getFormBuilderActions();

  const section = formDefinition.sections[currentSectionIndex];

  if (!section) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <p className="text-muted-foreground text-sm">No sections in this form.</p>
      </div>
    );
  }

  const subsection = section.subsections[currentSubsectionIndex];

  return (
    <div className="bg-muted flex-1 overflow-y-auto pb-20">
      <div className="mx-auto max-w-4xl p-6 md:p-8">
        <div className="mb-2">
          <Badge
            variant="secondary"
            className="text-xs"
          >
            Preview
          </Badge>
        </div>

        <h1 className="text-foreground mb-6 text-2xl font-bold">
          {section.title || 'Untitled Section'}
        </h1>

        {section.subsections.length > 0 && (
          <div className="mb-6 flex gap-2">
            {section.subsections.map((subsectionItem, subsectionIndex) => (
              <div
                key={subsectionItem.id}
                className="flex-1 cursor-pointer"
                onClick={() => navigateTo(currentSectionIndex, subsectionIndex)}
              >
                <div
                  className={cn(
                    'h-2 rounded-full transition-colors',
                    subsectionIndex === currentSubsectionIndex ? 'bg-primary' : 'bg-primary/20',
                  )}
                />
                <p
                  className={cn(
                    'mt-1 truncate text-xs',
                    subsectionIndex === currentSubsectionIndex
                      ? 'text-primary font-medium'
                      : 'text-muted-foreground',
                  )}
                >
                  {subsectionItem.title || 'Untitled'}
                </p>
              </div>
            ))}
          </div>
        )}

        {!subsection ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground text-sm">This section has no subsections.</p>
          </Card>
        ) : (
          <PreviewNavigationProvider
            formDefinition={formDefinition}
            formData={formData}
            onFormDataChange={onFormDataChange}
          >
            <Card className="p-0">
              <div className="p-6">
                <div className="space-y-8">
                  <PolicyApplicationFormSection
                    subsection={subsection}
                    formData={formData}
                    questionRegistry={formDefinition.questions}
                    onSubmissionChange={onFormDataChange}
                  />
                </div>

                <div className="mt-8 flex items-center justify-end gap-3 border-t pt-4">
                  <Button
                    variant="outline"
                    disabled={!canGoPrevious}
                    onClick={navigatePrevious}
                  >
                    <ChevronLeft className="mr-1 size-4" />
                    Previous
                  </Button>
                  <Button
                    disabled={!canGoNext}
                    onClick={navigateNext}
                  >
                    Next
                    <ChevronRight className="ml-1 size-4" />
                  </Button>
                </div>
              </div>
            </Card>
          </PreviewNavigationProvider>
        )}
      </div>
    </div>
  );
}
